import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Close, ZoomOut } from './ui/icons';

type Point = { x: number; y: number };

/** Both pointers of a pinch, or undefined unless exactly two are down. */
function twoPointers(map: Map<number, Point>): [Point, Point] | undefined {
  if (map.size !== 2) return undefined;
  const [a, b] = [...map.values()];
  return a && b ? [a, b] : undefined;
}

/** Vertical drag at 1× beyond this closes the viewer. */
const SWIPE_CLOSE_PX = 80;

/**
 * Full-screen image viewer (M4.5): pinch to zoom (two pointers), drag to pan when zoomed,
 * double-tap to toggle 1× / 2.5×. Closes on the corner button (inside the safe area), a tap
 * on the backdrop, a swipe down at 1×, Escape and the browser Back button (a history state is
 * pushed on open). Focus is trapped inside while open and returned to the thumbnail after.
 */
export function ImageViewer({
  src,
  alt,
  onClose,
  returnFocusTo,
}: {
  src: string;
  alt: string;
  onClose: () => void;
  /** The thumbnail that opened the viewer; focus returns to it on close. */
  returnFocusTo?: RefObject<HTMLElement | null> | undefined;
}) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const state = useRef({ scale: 1, x: 0, y: 0 });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{
    dist: number;
    scale: number;
    x: number;
    y: number;
    cx: number;
    cy: number;
  } | null>(null);
  const lastTap = useRef(0);
  const swipe = useRef<{ startY: number; startX: number; dy: number; moved: boolean } | null>(null);
  const historyPushed = useRef(false);
  const closing = useRef(false);

  const apply = () => {
    const { scale, x, y } = state.current;
    if (imgRef.current)
      imgRef.current.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
  };
  const setScale = (scale: number, cx = 0, cy = 0) => {
    const s = state.current;
    const next = Math.min(5, Math.max(1, scale));
    if (next === 1) {
      s.x = 0;
      s.y = 0;
    } else {
      const k = next / s.scale;
      s.x = cx - (cx - s.x) * k;
      s.y = cy - (cy - s.y) * k;
    }
    s.scale = next;
    apply();
  };

  /** Close once: through the history entry when we pushed one, else directly. */
  const close = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    if (historyPushed.current && window.history.state?.pa_viewer) {
      historyPushed.current = false;
      window.history.back();
      return;
    }
    onClose();
  }, [onClose]);

  // Open: focus the close button, push a history state so Back closes; close on popstate.
  useEffect(() => {
    const previous = returnFocusTo?.current ?? (document.activeElement as HTMLElement | null);
    closeRef.current?.focus({ preventScroll: true });
    try {
      window.history.pushState({ ...(window.history.state ?? {}), pa_viewer: true }, '');
      historyPushed.current = true;
    } catch {
      historyPushed.current = false;
    }
    const onPop = () => {
      historyPushed.current = false;
      onClose();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Closed by a route change or the parent: drop the history entry we own.
      if (historyPushed.current && window.history.state?.pa_viewer) {
        historyPushed.current = false;
        window.history.back();
      }
      previous?.focus?.({ preventScroll: true });
    };
  }, [onClose, returnFocusTo]);

  // Escape closes; Tab stays inside the dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0] as HTMLElement;
        const last = focusable[focusable.length - 1] as HTMLElement;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [close]);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pair = twoPointers(pointers.current);
    if (pair) {
      swipe.current = null;
      const [a, b] = pair;
      gesture.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        scale: state.current.scale,
        x: state.current.x,
        y: state.current.y,
        cx: (a.x + b.x) / 2 - window.innerWidth / 2,
        cy: (a.y + b.y) / 2 - window.innerHeight / 2,
      };
    } else if (pointers.current.size === 1) {
      swipe.current = { startX: e.clientX, startY: e.clientY, dy: 0, moved: false };
      const now = Date.now();
      if (now - lastTap.current < 300) {
        setScale(
          state.current.scale > 1 ? 1 : 2.5,
          e.clientX - window.innerWidth / 2,
          e.clientY - window.innerHeight / 2,
        );
        lastTap.current = 0;
        swipe.current = null;
      } else lastTap.current = now;
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pair = twoPointers(pointers.current);
    if (pair && gesture.current) {
      const [a, b] = pair;
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      state.current.x = gesture.current.x;
      state.current.y = gesture.current.y;
      state.current.scale = gesture.current.scale;
      setScale(
        (gesture.current.scale * dist) / gesture.current.dist,
        gesture.current.cx,
        gesture.current.cy,
      );
    } else if (pointers.current.size === 1 && state.current.scale > 1) {
      state.current.x += e.clientX - prev.x;
      state.current.y += e.clientY - prev.y;
      apply();
    } else if (pointers.current.size === 1 && swipe.current) {
      // At 1×: a vertical drag follows the finger and closes past the threshold.
      const dy = e.clientY - swipe.current.startY;
      const dx = e.clientX - swipe.current.startX;
      if (Math.abs(dy) > 4 || Math.abs(dx) > 4) swipe.current.moved = true;
      swipe.current.dy = dy;
      if (dy > 0 && imgRef.current) {
        imgRef.current.style.transform = `translate(0px, ${dy}px) scale(${Math.max(0.85, 1 - dy / 1200)})`;
        imgRef.current.style.opacity = String(Math.max(0.4, 1 - dy / 400));
      }
    }
  };
  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) gesture.current = null;
    const s = swipe.current;
    if (s && pointers.current.size === 0) {
      swipe.current = null;
      if (s.dy > SWIPE_CLOSE_PX) {
        close();
        return;
      }
      if (imgRef.current) {
        imgRef.current.style.opacity = '';
        apply();
      }
      // A tap on the backdrop (not the image) at 1× closes.
      const onImage = imgRef.current?.contains(e.target as Node);
      if (!s.moved && !onImage && state.current.scale === 1 && lastTap.current) {
        lastTap.current = 0;
        close();
      }
    }
  };

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={alt || t('two.image.viewer')}
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-ink/90 touch-none select-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={false}
        className="max-h-full max-w-full object-contain will-change-transform"
      />
      <div className="absolute end-3 top-3 flex gap-2 pt-safe">
        <button
          type="button"
          onClick={() => setScale(1)}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={t('two.image.reset')}
          className="flex size-11 items-center justify-center rounded-md bg-surface/90 text-ink"
        >
          <ZoomOut size={20} />
        </button>
        <button
          ref={closeRef}
          type="button"
          onClick={close}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={t('common.close')}
          className="flex size-11 items-center justify-center rounded-md bg-surface/90 text-ink"
        >
          <Close size={20} />
        </button>
      </div>
    </div>
  );
}
